import Add16 from '@carbon/icons-react/es/add/16';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import { sortBy } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'components/atoms/button';
import { IconShell } from 'components/atoms/icon-shell';
import { Loading } from 'components/atoms/loading';
import {
  Empty,
  Loader,
} from 'components/organisms/projects/project-sidebar/spaces-menu/styles';
import { PermissionRuleName } from 'constant';
import { NewSpace } from '../new-space';
import { SpaceSubMenuItem } from '../space-menu-item/space-menu-item';
import { MenuGroupTitle } from './menu-group-title';
import { Menu, ExpandIconContainer, MenuGroupItem } from './menu-group.styles';
import { MenuGroupTypes } from './menu-group.types';
import { useMenugroupActions } from './use-menu-group-actions';
import { generateTabMenuKey } from './utils';

export const MenuGroup: FC<MenuGroupTypes> = ({
  project,
  activeSpaceId,
  activeSpaceTabId,
  title,
  tooltipTitle,
  projectSpaces = [],
  isLoading,
  onSpaceAdd,
  isPrivate,
  ...rest
}) => {
  const {
    isNoProjectSpaces,
    defaultOpenKeys,
    creatingSpace,
    openKeys,
    renderIcon,
    setOpenKeys,
    onSpaceCreate,
    onSpaceEditCancel,
    onOpenChange,
  } = useMenugroupActions({
    project,
    projectSpaces,
  });

  const [collapse, setCollapse] = useState(true);
  const sortedProjectSpaces = useMemo(
    () => sortBy(projectSpaces, 'spaceId')?.filter((space) => !space.hidden),
    [projectSpaces],
  );

  useEffect(() => {
    if (openKeys.length === 0) {
      setCollapse(false);

      return;
    }
    setCollapse(true);
  }, [openKeys]);
  const showSearchEmpty = useMemo(() => {
    return projectSpaces.length && sortedProjectSpaces.length === 0;
  }, [projectSpaces, sortedProjectSpaces]);

  return (
    <Menu
      {...rest}
      mode="inline"
      defaultOpenKeys={defaultOpenKeys}
      openKeys={collapse ? openKeys : []}
      onOpenChange={(key) => {
        onOpenChange(key);
      }}
      defaultSelectedKeys={[
        activeSpaceId,
        generateTabMenuKey(activeSpaceId, activeSpaceTabId),
      ]}
      selectedKeys={[
        activeSpaceId,
        generateTabMenuKey(activeSpaceId, activeSpaceTabId),
      ]}
      expandIcon={({ isOpen }) => {
        return (
          <ExpandIconContainer>
            {isOpen ? <ChevronDown16 /> : <ChevronRight16 />}
          </ExpandIconContainer>
        );
      }}
    >
      <MenuGroupItem
        title={
          <MenuGroupTitle
            collapseAll={() => {
              setCollapse(!collapse);
              onOpenChange(defaultOpenKeys);
            }}
            collapse={collapse}
            title={title}
            tooltipTitle={tooltipTitle}
            onSpaceAdd={onSpaceAdd}
            isNoProjectSpaces={isNoProjectSpaces}
            isPrivate={isPrivate ?? false}
          />
        }
      />

      {isLoading ? (
        <Loader>
          <Loading />
          <FormattedMessage id="loading" />
        </Loader>
      ) : null}

      {isNoProjectSpaces ? (
        <Button
          onClick={() => onSpaceAdd(isPrivate)}
          icon={<Add16 />}
          type="link"
          block
          ruleName={
            isPrivate ? undefined : PermissionRuleName.SpaceSharedCreateAction
          }
        >
          <FormattedMessage
            id={isPrivate ? 'space.private.add' : 'space.shared.add'}
          />
        </Button>
      ) : null}

      {showSearchEmpty && (
        <Empty ellipsis>
          <FormattedMessage id="projects.spaces.search.empty_text" />
        </Empty>
      )}

      {!isLoading && !isNoProjectSpaces
        ? projectSpaces.map((space) => (
            <>
              {space.spaceId === 0 ? (
                <Menu.Item
                  key={space.spaceId}
                  icon={<IconShell size="small" icon={renderIcon(space)} />}
                >
                  <NewSpace
                    space={space}
                    onChange={onSpaceCreate}
                    onCancel={onSpaceEditCancel}
                    isLoading={creatingSpace}
                  />
                </Menu.Item>
              ) : (
                <SpaceSubMenuItem
                  key={space.spaceId}
                  activeSpaceId={Number(activeSpaceId)}
                  projectId={project.projectId}
                  space={space}
                  activeSpaceTabId={activeSpaceTabId}
                  setOpenKeys={(keys) => setOpenKeys((v) => [...v, ...keys])}
                />
              )}
            </>
          ))
        : null}
    </Menu>
  );
};
